import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { AnchorNodeData } from '../tree/tree.model';
import { ChangeRequest } from 'src/models/change-request-dto/change-request.dto';

// Primeng
// eslint-disable-next-line sort-imports
import { Popover, PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import type { TreeNode } from 'primeng/api';

interface ChangeRequestDetailRow {
    field: string;
    value: string | number;
}

@Component({
    selector: 'app-change-request-details',
    templateUrl: './change-request-details.component.html',
    styleUrls: ['./change-request-details.component.scss'],
    imports: [CommonModule, ButtonModule, PopoverModule, TableModule, TooltipModule],
})
export class ChangeRequestDetailsComponent implements OnDestroy {
    @ViewChild('changeRequestPopover') private changeRequestPopover!: Popover;

    public changeRequestDetails: ChangeRequestDetailRow[] = [];

    private popoverTarget?: HTMLElement;
    private removeDocumentClickListener?: () => void;
    private readonly document = inject(DOCUMENT);

    public constructor() {
        const onDocumentClickCapture = (event: MouseEvent): void => {
            this.handleDocumentClick(event);
        };

        this.document.addEventListener('click', onDocumentClickCapture, true);
        this.removeDocumentClickListener = (): void => {
            this.document.removeEventListener('click', onDocumentClickCapture, true);
        };
    }

    public close(): void {
        this.changeRequestPopover.hide();
    }

    public ngOnDestroy(): void {
        this.removeDocumentClickListener?.();
    }

    public open(node: TreeNode<AnchorNodeData>, event?: Event, target?: HTMLElement): void {
        const anchor = node.data?.anchor;
        if (!this.isChangeRequest(anchor)) {
            this.changeRequestDetails = [];
            this.changeRequestPopover?.hide();
            return;
        }

        this.popoverTarget = this.getAnchorTarget(target ?? this.getEventTargetElement(event));
        this.changeRequestDetails = this.createDetailRows(anchor);
        this.changeRequestPopover.show(event, this.popoverTarget);
    }

    private createDetailRows(changeRequest: ChangeRequest): ChangeRequestDetailRow[] {
        return [
            { field: 'Title', value: changeRequest.title || changeRequest.name },
            { field: 'Status', value: changeRequest.crStatus },
            {
                field: 'SOP Date',
                value: changeRequest.sopString,
            },
            { field: 'Test Iteration', value: changeRequest.testIteration },
            { field: 'Architect', value: changeRequest.architect },
            { field: 'Comment', value: changeRequest.logText || '-' },
        ];
    }

    private getAnchorTarget(target: unknown): HTMLElement | undefined {
        if (!(target instanceof HTMLElement)) return undefined;

        const treeNodeContent = target.closest('.p-tree-node-content');
        return treeNodeContent instanceof HTMLElement ? treeNodeContent : target;
    }

    private getEventTargetElement(event?: Event): HTMLElement | undefined {
        const currentTarget = event?.currentTarget;
        if (currentTarget instanceof HTMLElement) return currentTarget;

        const target = event?.target;
        return target instanceof HTMLElement ? target : undefined;
    }

    private handleDocumentClick(event: MouseEvent): void {
        const container = this.changeRequestPopover?.container;
        if (!this.changeRequestPopover?.overlayVisible || !container) return;

        const target = event.target;
        if (target instanceof Node && container.contains(target)) return;

        this.close();
    }

    private isChangeRequest(value: unknown): value is ChangeRequest {
        const changeRequest = value as Partial<ChangeRequest> | undefined;
        return (
            typeof changeRequest?.key === 'number' &&
            typeof changeRequest.name === 'string' &&
            typeof changeRequest.architect === 'string' &&
            typeof changeRequest.crStatus === 'number' &&
            typeof changeRequest.title === 'string'
        );
    }
}
